import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import type { ChartGranularity } from '@/lib/chart-axis';
import {
  partialEdges,
  partialLabel,
  type ChartRange,
  type PartialEdges,
} from '@/lib/partial-buckets';

export type PartialBuckets = {
  edges: PartialEdges;
  /** Row indexes of the partial buckets (for the hatched bars). */
  indexes: number[];
  /** Labels next to the first/last partial bucket (CHART-03). */
  labels: { first?: string; last?: string };
};

const NONE: PartialBuckets = {
  edges: { first: false, last: false },
  indexes: [],
  labels: {},
};

/**
 * Partial first/last bucket of a chart's rows for the filter's period and
 * granularity (CHART-01, CHART-03). Without a period nothing is partial.
 */
export function usePartialBuckets(
  rows: ReadonlyArray<Record<string, unknown>>,
  range: ChartRange | undefined,
  granularity: ChartGranularity
): PartialBuckets {
  const { LL, locale } = useI18nContext();
  const from = range?.from;
  const to = range?.to;

  return useMemo(() => {
    if (from === undefined || to === undefined) return NONE;
    const starts = rows.flatMap((row) =>
      typeof row.date === 'string' ? [row.date] : []
    );
    if (starts.length !== rows.length || starts.length === 0) return NONE;

    const period = { from, to };
    const edges = partialEdges(starts, period, granularity);
    const now = DateTime.now();
    const lastIndex = rows.length - 1;
    return {
      edges,
      indexes: [
        ...(edges.first ? [0] : []),
        ...(edges.last && (lastIndex > 0 || !edges.first) ? [lastIndex] : []),
      ],
      labels: {
        first: edges.first
          ? partialLabel('first', granularity, period, now, LL, locale)
          : undefined,
        last: edges.last
          ? partialLabel('last', granularity, period, now, LL, locale)
          : undefined,
      },
    };
  }, [rows, from, to, granularity, LL, locale]);
}
